import * as XLSX from 'xlsx';
import { SiteData } from '../types';

// Column mapping from Excel to SiteData interface
const EXCEL_COLUMN_MAP = {
  'SiteName': 'siteName',
  'IsOnline': 'isOnline',
  'Customer': 'customer',
  'Country': 'country',
  'Region': 'region',
  'SiteType': 'siteType',
  'MLVersion': 'mlVersion',
  'DBVersion': 'dbVersion',
  'SiteInstallDate': 'siteInstallDate',
  'SiteLicenseExpiration': 'siteLicenseExpiration',
  'SiteLicenseExpirationInML': 'siteLicenseExpirationInML',
  'StartUsage': 'startUsage',
  'LastUsage': 'lastUsage',
  'MonthsInUse': 'monthsInUse',
  'UsedLastMonth': 'usedLastMonth',
  'SimulatorsConnected': 'simulatorsConnected',
  'RegisteredUsers': 'registeredUsers',
  'ActiveUsers': 'activeUsers',
  'InactiveUsers': 'inactiveUsers',
  'NumSessionsLastMonth': 'numSessionsLastMonth',
  'NumSessionsOnServer': 'numSessionsOnServer',
  'TotalSimulationTimeMinutes': 'totalSimulationTimeMinutes',
  'NumSessionsOnServerLast12Month': 'numSessionsOnServerLast12Month',
  'SimulationTimeMinutesLast12Month': 'simulationTimeMinutesLast12Month',
  'ActiveUsers12Months': 'activeUsers12Months',
  'WRegion': 'wRegion'
};

// Required fields for dashboard functionality
const REQUIRED_FIELDS = [
  'SiteName', 'IsOnline', 'Customer', 'Country', 'Region', 'SiteType',
  'MLVersion', 'LastUsage', 'NumSessionsLastMonth', 'NumSessionsOnServerLast12Month',
  'SimulatorsConnected', 'SiteLicenseExpiration'
];

interface ParseResult {
  data: SiteData[];
  missingFields: string[];
  warnings: string[];
}

export const parseExcelToSiteData = (file: File): Promise<ParseResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          reject(new Error('Excel file must contain at least a header row and one data row'));
          return;
        }
        
        const headers = jsonData[0] as string[];
        const missingFields: string[] = [];
        const warnings: string[] = [];
        
        // Check for required fields
        REQUIRED_FIELDS.forEach(field => {
          if (!headers.includes(field)) {
            missingFields.push(field);
          }
        });
        
        if (missingFields.length > 0) {
          reject(new Error(`Missing required fields: ${missingFields.join(', ')}`));
          return;
        }
        
        // Parse data rows
        const siteData: SiteData[] = [];
        
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          const site: any = {};
          
          headers.forEach((header, index) => {
            const mappedField = EXCEL_COLUMN_MAP[header as keyof typeof EXCEL_COLUMN_MAP];
            if (mappedField && row[index] !== undefined && row[index] !== null) {
              let value = row[index];
              
              // Type conversions
              if (mappedField === 'isOnline' || mappedField === 'usedLastMonth') {
                value = Boolean(value === 'TRUE' || value === true || value === 1 || value === 'Yes');
              } else if (typeof value === 'number' && [
                'monthsInUse', 'simulatorsConnected', 'registeredUsers', 'activeUsers', 
                'inactiveUsers', 'numSessionsLastMonth', 'numSessionsOnServer', 
                'totalSimulationTimeMinutes', 'numSessionsOnServerLast12Month', 
                'simulationTimeMinutesLast12Month', 'activeUsers12Months'
              ].includes(mappedField)) {
                value = Math.floor(Number(value));
              } else if ([
                'siteInstallDate', 'siteLicenseExpiration', 'siteLicenseExpirationInML', 
                'startUsage', 'lastUsage'
              ].includes(mappedField)) {
                // Handle date formats more robustly
                if (typeof value === 'number') {
                  try {
                    // Excel date serial number
                    const date = XLSX.SSF.parse_date_code(value);
                    value = `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
                  } catch {
                    // If Excel date parsing fails, leave as empty string
                    value = '';
                  }
                } else if (typeof value === 'string' && value.trim() !== '') {
                  try {
                    // Try to parse as date string and validate
                    const parsedDate = new Date(value.trim());
                    if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 1900 && parsedDate.getFullYear() < 2100) {
                      value = parsedDate.toISOString().split('T')[0];
                    } else {
                      // Invalid date, leave as empty string
                      value = '';
                    }
                  } catch {
                    // If parsing fails, leave as empty string
                    value = '';
                  }
                } else {
                  // Empty or null value
                  value = '';
                }
              }
              
              site[mappedField] = value;
            }
          });
          
          // Validate required fields for this row
          const missingRowFields = REQUIRED_FIELDS.filter(field => {
            const mappedField = EXCEL_COLUMN_MAP[field as keyof typeof EXCEL_COLUMN_MAP];
            return !site[mappedField];
          });
          
          if (missingRowFields.length > 0) {
            warnings.push(`Row ${i + 1}: Missing values for ${missingRowFields.join(', ')}`);
          } else {
            siteData.push(site as SiteData);
          }
        }
        
        if (siteData.length === 0) {
          reject(new Error('No valid data rows found in Excel file'));
          return;
        }
        
        resolve({
          data: siteData,
          missingFields,
          warnings
        });
        
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};